import * as React from "react";

import { SectionTitle } from "./section-title";

/**
 * @deprecated Use `<SectionTitle variant="plain" …>` instead. This is now a thin
 * back-compat wrapper that delegates to the one canonical titling component
 * (`section-title.tsx`) so the whole app renders titles through a single
 * implementation. New code should import `SectionTitle` directly:
 *
 *   // before
 *   <SectionHeading size="lg" title="Goals" description="…" actions={…} />
 *   // after
 *   <SectionTitle variant="plain" size="lg" title="Goals" subtitle="…" action={…} />
 *
 * Prop mapping: `description → subtitle`, `actions → action`. Everything else
 * (size, as, className, titleClassName) passes straight through.
 *
 * The size tiers are unchanged (lg → type-title, md → type-body, sm → type-body,
 * default `sm`), all below the PageNav page title so a heading never outsizes
 * its page. See `SectionTitle` for the full system.
 */
export type SectionHeadingSize = "lg" | "md" | "sm";

export interface SectionHeadingProps {
  /** The section title. */
  title: React.ReactNode;
  /** Optional muted description line below the title. */
  description?: React.ReactNode;
  /** Optional right-aligned slot for counts, links, or actions. */
  actions?: React.ReactNode;
  /** Visual tier on the type scale. Default `sm`. */
  size?: SectionHeadingSize;
  /** Heading element to render. Default `h2`. */
  as?: "h2" | "h3" | "h4";
  /** Extra classes on the wrapper. */
  className?: string;
  /** Extra classes on the heading element itself. */
  titleClassName?: string;
}

export function SectionHeading({
  title,
  description,
  actions,
  size = "sm",
  as = "h2",
  className,
  titleClassName,
}: SectionHeadingProps) {
  return (
    <SectionTitle
      variant="plain"
      title={title}
      subtitle={description}
      action={actions}
      size={size}
      as={as}
      className={className}
      titleClassName={titleClassName}
    />
  );
}
